import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addUser, editUser, getUserById } from '../../../api/endpoints/system';
import { USER_TYPE_LABELS, type SystemUser, type UserTypeCode } from '../../../api/types/system';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { useFreshEditRow } from '../../../components/useFreshEditRow';
import { PhoneField } from '../../../components/PhoneField';

const schema = z.object({
  username: z.string().min(1, 'Kullanıcı adı gerekli'),
  firstName: z.string().min(1, 'Ad gerekli'),
  lastName: z.string().min(1, 'Soyad gerekli'),
  email: z.string().optional(),
  phone: z.string().optional(),
  gsaId: z.coerce.number().min(1, 'Firma ID gerekli'),
  userType: z.coerce.number().min(0).max(4),
  arrangerCode: z.string().optional(),
  expenseCenter: z.string().optional(),
  agencyCode: z.string().optional(),
  currency: z.string().optional(),
  language: z.string().optional(),
  isActive: z.boolean(),
  isTwoFactorAuthenticationActive: z.boolean(),
  threeDPayment: z.boolean(),
  creditCardPayment: z.boolean(),
  currentPayment: z.boolean(),
  isDefaultServiceFee: z.boolean(),
  isExpCenterCreditAllowed: z.boolean(),
  isChangesAllowed: z.boolean(),
  generatePassword: z.boolean(),
  password: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  gsaId: 0,
  userType: 2,
  arrangerCode: '',
  expenseCenter: '',
  agencyCode: '',
  currency: '',
  language: '',
  isActive: true,
  isTwoFactorAuthenticationActive: false,
  threeDPayment: false,
  creditCardPayment: false,
  currentPayment: false,
  isDefaultServiceFee: false,
  isExpCenterCreditAllowed: false,
  isChangesAllowed: false,
  generatePassword: false,
  password: '',
};

function toFormValues(u: SystemUser): FormValues {
  return {
    username: u.username ?? '',
    firstName: u.firstName ?? '',
    lastName: u.lastName ?? '',
    email: u.email ?? '',
    phone: u.phone ?? '',
    gsaId: u.gsaId ?? 0,
    userType: (u.userType ?? 2) as number,
    arrangerCode: u.arrangerCode ?? '',
    expenseCenter: u.expenseCenter ?? '',
    agencyCode: u.agencyCode ?? '',
    currency: u.currency ?? '',
    language: u.language ?? '',
    isActive: !!u.isActive,
    isTwoFactorAuthenticationActive: !!u.isTwoFactorAuthenticationActive,
    threeDPayment: !!u.threeDPayment,
    creditCardPayment: !!u.creditCardPayment,
    currentPayment: !!u.currentPayment,
    isDefaultServiceFee: !!u.isDefaultServiceFee,
    isExpCenterCreditAllowed: !!u.isExpCenterCreditAllowed,
    isChangesAllowed: !!u.isChangesAllowed,
    generatePassword: false,
    password: '',
  };
}

const permissionFields: { name: keyof FormValues; label: string }[] = [
  { name: 'isActive', label: 'Aktif' },
  { name: 'isTwoFactorAuthenticationActive', label: 'İki Faktörlü Doğrulama' },
  { name: 'threeDPayment', label: '3D Ödeme' },
  { name: 'creditCardPayment', label: 'Kredi Kartı ile Ödeme' },
  { name: 'currentPayment', label: 'Cari Hesap ile Ödeme' },
  { name: 'isDefaultServiceFee', label: 'Varsayılan Servis Ücreti' },
  { name: 'isExpCenterCreditAllowed', label: 'Masraf Merkezi Kredisi' },
  { name: 'isChangesAllowed', label: 'Değişikliklere İzin Ver' },
];

/** Create/edit dialog for Kullanıcı (system user). `UserModel`'s advanced fee-override
 *  fields (FixServiceFee/FlexServiceFee/ExtraDomFee/ExtraIntFee and their *Percent
 *  counterparts) are intentionally NOT exposed here — out of scope for basic user admin per
 *  the migration guide's page inventory. On edit they're preserved as-is by spreading the
 *  original row into the request before applying form values (see mutationFn below), so
 *  saving this form never wipes them.
 *
 *  Password is write-only: `password` always comes back `null` from GetUsers (confirmed live),
 *  so the field starts blank on edit and is only included in the request when the admin
 *  actually types a new value — leaving it blank keeps the existing password untouched. */
export function UserFormDialog({
  open,
  user,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  user: SystemUser | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!user;

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Plaintext auto-generated password from `User/AddUser` — shown once, copyable, after a
  // successful create (the legacy welcome-email path is dead server-side).
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  // Refetch the fresh row on edit-open; silently falls back to the list row on error
  // (Get*ById can 200 with a business-logic null-ref for some accounts' data).
  const seed = useFreshEditRow({
    open,
    editing: user,
    queryKey: ['system', 'user', user?.id],
    fetch: async (row) => (await getUserById(row.id)).userList?.[0],
  });

  useEffect(() => {
    if (open) {
      reset(seed ? toFormValues(seed) : emptyValues);
      setGeneratedPassword(null);
    }
  }, [open, seed, reset]);

  const generatePassword = watch('generatePassword');

  const mutation = useMutation({
    mutationFn: ({ generatePassword: genPw, password, ...rest }: FormValues) => {
      if (isEdit && user) {
        const payload: SystemUser = { ...(seed ?? user), ...rest, userType: rest.userType as UserTypeCode };
        if (password) payload.password = password;
        return editUser(payload);
      }
      return addUser({
        ...rest,
        userType: rest.userType as UserTypeCode,
        generatePassword: genPw,
        password: genPw ? undefined : password,
      });
    },
    onSuccess: (res: unknown) => {
      enqueueSnackbar(isEdit ? 'Kullanıcı güncellendi.' : 'Kullanıcı oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['system', 'users'] });
      const pw =
        !isEdit && res && typeof res === 'object' && 'generatedPassword' in res
          ? (res as { generatedPassword?: string | null }).generatedPassword
          : undefined;
      if (pw) {
        setGeneratedPassword(pw);
      } else {
        onClose();
      }
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const copyPassword = () => {
    if (generatedPassword) {
      navigator.clipboard?.writeText(generatedPassword).then(
        () => enqueueSnackbar('Şifre kopyalandı.', { variant: 'success' }),
        () => enqueueSnackbar('Kopyalama başarısız.', { variant: 'error' })
      );
    }
  };

  const submit = handleSubmit((values) => mutation.mutate(values));

  if (generatedPassword) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Kullanıcı oluşturuldu</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Geçici şifre yalnızca bir kez gösterilir — kullanıcıya iletin.
          </Alert>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              component="code"
              sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'action.hover', fontSize: 16, letterSpacing: 1, flex: 1 }}
            >
              {generatedPassword}
            </Box>
            <IconButton onClick={copyPassword} aria-label="Şifreyi kopyala">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Kullanıcıyı Güncelle' : 'Yeni Kullanıcı'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Temel Bilgiler</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Kullanıcı Adı"
                error={!!errors.username}
                helperText={errors.username?.message}
                {...register('username')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="userType"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Kullanıcı Tipi (API kodu)">
                    {(Object.keys(USER_TYPE_LABELS) as unknown as UserTypeCode[]).map((code) => (
                      <MenuItem key={code} value={code}>
                        {USER_TYPE_LABELS[code]} ({code})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Ad"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                {...register('firstName')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Soyad"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                {...register('lastName')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="E-posta" {...register('email')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label="Telefon"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Firma ID (GsaId)"
                error={!!errors.gsaId}
                helperText={errors.gsaId?.message ?? 'Sistem > Firmalar listesindeki Id'}
                {...register('gsaId')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Para Birimi" {...register('currency')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Dil" {...register('language')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Arranger Kodu" {...register('arrangerCode')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Masraf Merkezi" {...register('expenseCenter')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Acente Kodu" {...register('agencyCode')} />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2">Yetkiler ve Ayarlar</Typography>
          <Grid container spacing={1}>
            {permissionFields.map(({ name, label }) => (
              <Grid key={name} size={{ xs: 12, sm: 6, md: 4 }}>
                <Controller
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      }
                      label={label}
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>

          <Divider />
          <Typography variant="subtitle2">Şifre</Typography>
          <Grid container spacing={2}>
            {!isEdit && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="generatePassword"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      }
                      label="Şifreyi otomatik oluştur"
                    />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={isEdit ? 'Yeni Şifre (değiştirmek için doldurun)' : 'Şifre'}
                disabled={!isEdit && generatePassword}
                {...register('password')}
              />
            </Grid>
          </Grid>

          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
